import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { linkMicrosoftAccount } from '../services/microsoftAuth';
import { isDevBypassAllowed, signInDevBypass } from '../services/devBypassService';
import logoSvg from '../assets/logo.svg';

const MicrosoftIcon = () => (
  <svg width="20" height="20" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
    <rect x="1" y="1" width="9" height="9" fill="#f25022" />
    <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
    <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
    <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
  </svg>
);

const EyeIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 0 1 0-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178Z" />
    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z" />
  </svg>
);

const EyeSlashIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 0 0 1.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.451 10.451 0 0 1 12 4.5c4.756 0 8.773 3.162 10.065 7.498a10.522 10.522 0 0 1-4.293 5.774M6.228 6.228 3 3m3.228 3.228 3.65 3.65m7.894 7.894L21 21m-3.228-3.228-3.65-3.65m0 0a3 3 0 1 0-4.243-4.243m4.242 4.242L9.88 9.88" />
  </svg>
);

const ExclamationCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="h-5 w-5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z" />
  </svg>
);

const LoginPage: React.FC = () => {
  const { signIn, loginWithMicrosoft, user, loading } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Microsoft SSO states
  const [msLoading, setMsLoading] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkEmail, setLinkEmail] = useState('');
  const [linkPassword, setLinkPassword] = useState('');
  const [linkCredential, setLinkCredential] = useState<any>(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState('');
  // Dev-only escape hatch — only visible on staging hosts / npm run dev.
  const [devLoading, setDevLoading] = useState(false);
  const showDevBypass = isDevBypassAllowed();

  const handleDevBypass = async () => {
    setError('');
    setDevLoading(true);
    try {
      await signInDevBypass();
      // signInDevBypass awaits the users/{uid} write, so the doc is now
      // committed. But onAuthStateChanged already fired the instant the
      // anonymous sign-in resolved — it raced ahead of the write, read no
      // doc, set user=null, and won't re-fire (the anon uid is unchanged).
      // A hard navigation re-runs AuthContext's user-doc read, which now
      // finds the doc and renders the dashboard. Without this the first
      // click appears to do nothing and the operator has to reload manually.
      window.location.assign('/dashboard');
    } catch (e) {
      const msg = (e as Error).message || 'Dev bypass failed.';
      // Common diagnostic: anonymous auth provider not enabled in console.
      setError(
        msg.includes('operation-not-allowed')
          ? 'Anonymous auth is not enabled in this Firebase project. Enable it in Authentication → Sign-in method → Anonymous.'
          : msg,
      );
    } finally {
      setDevLoading(false);
    }
  };

  if (!loading && user) return <Navigate to="/dashboard" replace />;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      await signIn(email, password);
    } catch (err: any) {
      let errorMessage = 'Invalid email or password. Please try again.';
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/user-not-found') {
        errorMessage = 'Invalid email or password.';
      } else if (err.code === 'auth/wrong-password') {
        errorMessage = 'Invalid password.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email format.';
      } else if (err.code === 'auth/too-many-requests') {
        errorMessage = 'Too many failed attempts. Please try again later.';
      }
      setError(errorMessage);
    } finally {
      setBusy(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setError('');
    setMsLoading(true);
    try {
      await loginWithMicrosoft();
    } catch (err: any) {
      if (err.message === 'ACCOUNT_LINK_REQUIRED') {
        setLinkEmail(err.email || '');
        setLinkCredential(err.credential || null);
        setShowLinkDialog(true);
      } else {
        setError(err.message || 'Microsoft sign-in failed.');
      }
    } finally {
      setMsLoading(false);
    }
  };

  const handleLinkAccount = async () => {
    if (!linkEmail || !linkPassword || !linkCredential) return;
    setLinkError('');
    setLinkBusy(true);
    try {
      await linkMicrosoftAccount(linkEmail, linkPassword, linkCredential);
      setShowLinkDialog(false);
    } catch (err: any) {
      setLinkError(err.message || 'Linking failed.');
    } finally {
      setLinkBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 flex items-center justify-center p-4">
      <div className="w-full max-w-md mx-auto">

        {/* Logo & Branding */}
        <div className="text-center mb-8 space-y-4">
          <div className="flex justify-center">
            <div className="w-16 h-16 rounded-xl flex items-center justify-center shadow-lg bg-white border-2" style={{ borderColor: '#011132' }}>
              <img
                src={logoSvg}
                alt="Rascal EPR Logo"
                className="w-12 h-12 object-contain"
              />
            </div>
          </div>
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ color: '#011132' }}>Rascal EPR</h1>
            <p className="text-sm font-medium" style={{ color: '#011132', opacity: 0.7 }}>Employee Performance Review · Rascal Republic Group</p>
          </div>
        </div>

        {/* Login Card */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">

          {/* Card Header */}
          <div className="p-8 text-white" style={{ background: 'linear-gradient(135deg, #011132 0%, #022050 100%)' }}>
            <h2 className="text-2xl font-bold mb-2">Welcome Back</h2>
            <p className="opacity-90">Sign in to access your EPR dashboard</p>
          </div>

          {/* Form */}
          <div className="p-8">
            <form onSubmit={handleSubmit} className="space-y-6">

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                  Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  placeholder="you@rascalrepublic.com"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                  style={{ '--tw-ring-color': '#011132' } as React.CSSProperties}
                />
              </div>

              {/* Password */}
              <div>
                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 focus:ring-2 focus:border-transparent transition-all text-gray-900 placeholder-gray-400"
                    style={{ '--tw-ring-color': '#011132' } as React.CSSProperties}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                  </button>
                </div>
              </div>

              {/* Error */}
              {error && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <span className="text-red-600 flex-shrink-0 mt-0.5"><ExclamationCircleIcon /></span>
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}

              {/* Submit */}
              <button
                type="submit"
                disabled={busy}
                className="w-full py-3 px-4 text-white font-bold rounded-lg shadow-lg transition-all transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                style={{
                  backgroundColor: '#011132',
                  backgroundImage: 'linear-gradient(135deg, #011132 0%, #022050 100%)'
                }}
                onMouseEnter={e => {
                  if (!busy) e.currentTarget.style.backgroundImage = 'linear-gradient(135deg, #022050 0%, #033068 100%)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.backgroundImage = 'linear-gradient(135deg, #011132 0%, #022050 100%)';
                }}
              >
                {busy ? 'Signing in...' : 'Sign In'}
              </button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white text-gray-400">or</span>
              </div>
            </div>

            {/* Microsoft 365 SSO Button */}
            <button
              type="button"
              onClick={handleMicrosoftLogin}
              disabled={msLoading || busy}
              className="w-full flex items-center justify-center gap-3 py-3 px-4 rounded-lg border-2 border-gray-200 bg-white text-gray-700 font-semibold text-sm hover:border-gray-300 hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <MicrosoftIcon />
              {msLoading ? 'Signing in with Microsoft...' : 'Sign in with Microsoft 365'}
            </button>

            {/* DEV: staging-only escape hatch — see devBypassService.ts.
                Hidden on production hosts. Anonymous Firebase auth +
                superadmin /users doc upsert; lets the operator
                dogfood the app when SSO is misconfigured. */}
            {showDevBypass && (
              <div className="mt-3 pt-3 border-t border-dashed border-amber-300">
                <button
                  type="button"
                  onClick={handleDevBypass}
                  disabled={devLoading || busy || msLoading}
                  className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg border border-amber-400 bg-amber-50 text-amber-900 font-semibold text-xs hover:bg-amber-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <span aria-hidden="true">⚠</span>
                  {devLoading ? 'Signing in as Dev…' : 'Dev sign-in (staging only)'}
                </button>
                <p className="mt-1 text-[10px] text-amber-700 text-center leading-snug">
                  Anonymous Firebase auth + superadmin claim · only visible on staging
                </p>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="mt-8 text-center pb-4">
          <p className="text-[10px] text-gray-400">
            © 2025 Developed by RHW | Rascal Republic | Samara Lombok | Rinjani Bay Development
          </p>
        </footer>
      </div>

      {/* Account Linking Modal */}
      {showLinkDialog && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
            <div className="p-6 text-white" style={{ background: 'linear-gradient(135deg, #011132 0%, #022050 100%)' }}>
              <h3 className="text-lg font-bold">Link Your Account</h3>
              <p className="text-sm opacity-90 mt-1">This email is already registered. Enter your password to link your Microsoft account.</p>
            </div>
            <div className="p-6 space-y-4">
              <div className="bg-gray-50 rounded-lg px-4 py-3 text-sm text-gray-600 break-all">{linkEmail}</div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Password</label>
                <input
                  type="password"
                  value={linkPassword}
                  onChange={e => setLinkPassword(e.target.value)}
                  placeholder="Enter your existing password"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 text-gray-900"
                  onKeyDown={e => e.key === 'Enter' && handleLinkAccount()}
                />
              </div>
              {linkError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">{linkError}</p>
              )}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={() => { setShowLinkDialog(false); setLinkPassword(''); setLinkError(''); }}
                  className="flex-1 py-2.5 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleLinkAccount}
                  disabled={linkBusy || !linkPassword}
                  className="flex-1 py-2.5 rounded-lg text-white text-sm font-semibold transition-colors disabled:opacity-50"
                  style={{ backgroundColor: '#011132' }}
                >
                  {linkBusy ? 'Linking...' : 'Link & Sign In'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginPage;
